import gsap from 'gsap';
import HeroModule from '$src/js/modules/hero/HeroModule';
import { HeroState } from '$src/js/states/HeroState.svelte';

const CAMERA_START_Z = 7.5;
const CAMERA_END_Z = 5;
const VIDEO_START_SCALE = 1.0;
const VIDEO_END_SCALE = 1.15;

export interface IntroElements {
  contentEl: HTMLElement;
  overlayEl: HTMLElement;
  videoWrapperEl: HTMLElement;
  headerEl: HTMLElement;
  controlsEl: HTMLElement;
}

export class IntroService {
  private elements: IntroElements | null = null;
  private videoWrapperEl: HTMLElement | null = null;

  /**
   * Sets the video wrapper element (called from Hero before IntroScreen mounts).
   * @param el The video wrapper element.
   */
  readonly setVideoWrapperEl = (el: HTMLElement): void => {
    this.videoWrapperEl = el;
  };

  /**
   * Gets the video wrapper element.
   * @returns The video wrapper element.
   */
  readonly getVideoWrapperEl = (): HTMLElement | null => this.videoWrapperEl;

  /**
   * Sets all intro elements.
   * @param elements The intro elements.
   */
  readonly setElements = (elements: IntroElements): void => {
    this.elements = elements;
  };

  /**
   * Returns the initial camera Z position.
   * @returns The initial camera Z position.
   */
  readonly getCameraStartZ = (): number => CAMERA_START_Z;

  /**
   * Dismiss the intro screen
   */
  readonly dismiss = (): void => {
    if (!this.elements) {
      return;
    }

    const { contentEl, overlayEl, videoWrapperEl, headerEl, controlsEl } = this.elements;
    const sceneService = HeroModule.getInstance().scene;
    const camera = sceneService.getCamera();

    gsap.set(headerEl, { opacity: 0, y: -100 });
    gsap.set(controlsEl, { opacity: 0, y: 100 });

    // Init timeline
    const tl = gsap.timeline({
      onComplete: () => {
        HeroState.introVisible = false;
        HeroState.introDismissed = true;
      },
    });

    // Fade out Intro content (logo, text, button)
    tl.to(contentEl, {
      opacity: 0,
      duration: 0.5,
      ease: 'power2.inOut',
    });

    // Fade out black overlay + camera zoom + video zoom (simultaneous)
    tl.to(
      overlayEl,
      {
        opacity: 0,
        duration: 1.2,
        ease: 'power2.inOut',
      },
      '+=0.1',
    );

    // Camera zoom (simultaneous)
    if (camera) {
      tl.to(
        camera.position,
        {
          z: CAMERA_END_Z,
          duration: 2.4,
          ease: 'power2.out',
          onUpdate: () => {
            camera.lookAt(0, 0.5, 0);
            sceneService.markDirty();
          },
          onComplete: () => {
            document.body.style.overflow = 'auto';
          },
        },
        '<',
      );
    }

    // Video zoom (simultaneous)
    tl.to(
      videoWrapperEl,
      {
        scale: VIDEO_END_SCALE,
        duration: 2.4,
        ease: 'power2.out',
      },
      '<',
    );

    // Slide header down + fade in
    tl.to(
      headerEl,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
      },
      '<0.5',
    );

    // Slide controls up + fade in
    tl.to(
      controlsEl,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
      },
      '<',
    );
  };

  /**
   * Sets the initial video scale.
   */
  readonly setInitialVideoScale = (): void => {
    if (!this.elements) {
      return;
    }

    gsap.set(this.elements.videoWrapperEl, {
      scale: VIDEO_START_SCALE
    });
  };
}
